import { IonAccordion, IonAccordionGroup, IonButtons, IonItem, IonLabel, IonTitle, IonToolbar } from '@ionic/react';
import { useEffect, useState } from 'react';

import { AttachmentAction, AttachmentAssetType, IAttachment, IAttachmentContainer, IBlobWithAttributes, MediaType } from '../../models/dms/attachment.model';
import { Wrapper } from './SelectedVehicleAttachments.styles';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import VehicleAttachmentGrid from '../vehicleAttachmentGrid/VehicleAttachmentsGrid';
import MediaCaptureButton from '../mediaCapture/MediaCaptureButton';
import { logTransaction } from '../../common/log';
import { LogType } from '../../models/log.model';
import { addMedia, decodeInvalidCharacters, parseFilename } from '../../common/util';
import { addVehicleAttachmentAsync } from '../../store/vehicleAttachmentSlice';
import { useMediaModal } from '../../providers/MediaModalProvider';
import store from '../../store';

interface IComponentProps {}

const SelectedVehicleAttachments: React.FC<IComponentProps> = () => {
  const [filteredAttachments, setFilteredAttachments] = useState<IAttachment[]>();
  const [otherAttachments, setOtherAttachments] = useState<IAttachment[]>();
  const [photoAdding, setPhotoAdding] = useState(false);

  const { labourClocking } = useAppSelector((state) => state.technicianClocking);
  const { selectedVehicleAttachments, adhocAttachmentsCache, attachmentsProcessing } = useAppSelector((state) => state.vehicleAttachments);
  const { vehicleAttachmentQueue } = useAppSelector((state) => state.requestQueue);

  const {
    actions: { showMediaModal },
  } = useMediaModal();

  const dispatch = useAppDispatch();

  interface IFileNameLabels {
    jcode?: string;
    jtype?: string;
    ref?: string;
  }

  useEffect(() => {
    if (!attachmentsProcessing) {
      const newFilteredAttachments = selectedVehicleAttachments?.attachments?.filter((attachment) => {
        try {
          if (attachment.refType !== 'RO') return false;

          if (labourClocking?.jobData.jobCode) {
            const fileName = decodeInvalidCharacters(attachment.fileName || '');

            const parsedVersion3 = parseFilename(fileName);
            const parsedVersion2 = fileName.match(/^[\\/]?a-([A-Z0-9]+)(?:-([A-Z0-9]+))?(?:-(image|video))?-([a-fA-F0-9]{32})\.(\w+)$/i);

            if (parsedVersion3 != null) {
              // Match a-KEY-[[key=value]][[key=value]]-image|video-GUID.extension
              // eg. a-005KPT-[[jcode=wiper1]][[jtype=R]][[ref=clock]][[preq=12345]]-image-123e4567e89b12d3a456426614174000.jpg
              const labels = parsedVersion3.labels as IFileNameLabels;
              if (labels.jcode === labourClocking?.jobData.jobCode && labels.jtype === labourClocking?.jobData.jobType) {
                // Check if the job code and job type match
                return true;
              }
              return false;
            } else if (parsedVersion2 != null) {
              // Match a-REGO-JOB-image|video-GUID.extension
              // eg. a-005KPT-19B5302003-image-7294d048e12e440d904d7b8f7bdb88c9.jpeg
              const [, rego, jobCode, type, guid, extension] = parsedVersion2;
              if (rego && jobCode && type && guid && extension) {
                return labourClocking?.jobData.jobCode === jobCode;
              }
              return false;
            } else {
              // Match anything else
              // eg. a-005KPT.jpeg
              return false;
            }
          }

          return false;
        } catch (err: any) {
          return false;
        }
      });

      const otherAttachments = selectedVehicleAttachments?.attachments?.filter((attachment) => {
        if (attachment.refType !== 'RO') return false;

        if (labourClocking?.jobData.jobCode) {
          return !(newFilteredAttachments || []).some((filtered) => filtered.fileName === attachment.fileName);
        }
        return false;
      });

      const allAttachments = [
        ...(newFilteredAttachments || []),
        ...(vehicleAttachmentQueue?.filter((a) => a.data.repairOrderNumber === labourClocking?.repairOrder.repairOrderNumber?.toString()).flatMap((a) => a.data.attachments) || []).filter((a) => a.action === AttachmentAction.Add),
        ...(adhocAttachmentsCache || []),
      ] as IAttachment[];

      setFilteredAttachments(allAttachments?.sort((a, b) => a.lineNumber - b.lineNumber) || undefined);
      setOtherAttachments(otherAttachments?.sort((a, b) => a.lineNumber - b.lineNumber) || undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labourClocking?.jobData.jobCode, selectedVehicleAttachments?.attachments, vehicleAttachmentQueue, adhocAttachmentsCache, attachmentsProcessing]);

  const handleClickMedia = (attachment: IAttachment) => {
    if (attachment.action !== AttachmentAction.Add) {
      showMediaModal(attachment, attachment.assetType, attachment.key);
    }
  };

  const handleMediaCapture = async (file: IBlobWithAttributes, mediaType: MediaType, assetType: AttachmentAssetType, description: string, selectedVehicleAttachments: IAttachmentContainer) => {
    try {
      setPhotoAdding(true);
      const mediaAsAttachment = await addMedia(file, mediaType, assetType, description);

      if (!mediaAsAttachment) {
        throw new Error('Failed to create media attachment, attachment was undefined');
      }

      logTransaction(LogType.Info, `Storing vehicle attachment in state`);

      const labourClocking = store.getState().technicianClocking.labourClocking;
      if (labourClocking) {
        const repairOrderNumber = labourClocking.repairOrder?.repairOrderNumber || 0;
        const jobCode = labourClocking.jobData?.jobCode || '';
        const jobType = labourClocking.jobData?.jobType || '';
        const existingAttachments = selectedVehicleAttachments.attachments || [];

        await dispatch(
          addVehicleAttachmentAsync({
            key: selectedVehicleAttachments?.key || '',
            newAttachment: mediaAsAttachment,
            existingAttachments,
            assetType,
            repairOrderNumber,
            isAdhoc: labourClocking?.jobData?.isAdhoc || false,
            mediaRef: 'clock',
            jobType,
            jobCode,
          }),
        );
      }
    } catch (err: any) {
      logTransaction(LogType.Error, `${err}|${err?.message}`);
    } finally {
      setPhotoAdding(false);
    }
  };

  return (
    <Wrapper className="ion-margin-top">
      <IonToolbar color="primary" className="job-images">
        <IonTitle>
          {labourClocking?.jobData.jobType} / {labourClocking?.jobData.jobCode}
        </IonTitle>
        <IonButtons slot="end" className="ion-padding-end">
          <MediaCaptureButton
            onMediaCapture={(file, type) =>
              handleMediaCapture(file, type, selectedVehicleAttachments?.assetType || AttachmentAssetType.WorkshopVehicle, labourClocking?.jobData?.jobCode || '', selectedVehicleAttachments || ({} as IAttachmentContainer))
            }
            isLoading={photoAdding}
            color="secondary"
            popoverSide="top"
          />
        </IonButtons>
      </IonToolbar>

      <VehicleAttachmentGrid
        assetKey={selectedVehicleAttachments?.key || ''}
        assetType={selectedVehicleAttachments?.assetType || AttachmentAssetType.WorkshopVehicle}
        attachments={filteredAttachments || []}
        handleClickAttachment={(attachment) => handleClickMedia(attachment)}
      />

      <IonAccordionGroup expand="inset" className="ion-no-margin">
        <IonAccordion key="[other]" value="[other]" disabled={otherAttachments?.length === 0}>
          <IonItem slot="header" color="light">
            <IonLabel>Other Media</IonLabel>
          </IonItem>
          <div className="ion-padding" slot="content">
            <VehicleAttachmentGrid
              assetKey={selectedVehicleAttachments?.key || ''}
              assetType={selectedVehicleAttachments?.assetType || AttachmentAssetType.WorkshopVehicle}
              attachments={[...(otherAttachments || [])]}
              handleClickAttachment={(attachment) => handleClickMedia(attachment)}
            />
          </div>
        </IonAccordion>
      </IonAccordionGroup>
    </Wrapper>
  );
};

export default SelectedVehicleAttachments;
